import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import LockResetIcon from '@mui/icons-material/LockReset'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import BrandState from '../../components/BrandState'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type MouseEvent, useEffect, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import {
  type SettingUser,
  type SettingUserCreatePayload,
  type SettingUserUpdatePayload,
  useChangeSettingUserPasswordMutation,
  useCreateSettingUserMutation,
  useGetSettingRolesQuery,
  useGetSettingUsersQuery,
  useUpdateSettingUserMutation,
} from '../../store/api/settingsApi'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import {
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  SHOW_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
} from '../../theme/responsive'
import SettingsChangePasswordDialog from './SettingsChangePasswordDialog'
import SettingsUserDeleteDialog from './SettingsUserDeleteDialog'
import SettingsUserDialog, { type SettingsUserDialogForm } from './SettingsUserDialog'
import SettingsUserValidDialog from './SettingsUserValidDialog'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '../../utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

type ColumnKind = 'text' | 'date' | 'datetime'
type ColumnDef = {
  key: keyof SettingUser
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

const WIDTHS_TABLE_KEY = 'settings.account'

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'username', label: 'Username' },
  { key: 'description', label: 'Description' },
  { key: 'roleCode', label: 'Role Code', filter: 'checkboxes' },
  { key: 'roleName', label: 'Role Name', filter: 'checkboxes' },
  { key: 'email', label: 'Email' },
  { key: 'fullName', label: 'Full Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'validText', label: 'Valid Text', filter: 'checkboxes' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'datetime' },
  { key: 'personUpdate', label: 'Updated Person', filter: 'checkboxes' },
]

const actionToolbarBtnSx = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'Failed to save user. Please try again.'
  if (!('data' in error)) return 'Failed to save user. Please try again.'

  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save user. Please try again.'

  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string' && msg.trim()) return msg
    }
    return JSON.stringify(first)
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail)

  return 'Failed to save user. Please try again.'
}

const cellValue = (row: SettingUser, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  return String(val)
}

export default function SettingsAccountListView() {
  const { canEdit } = usePagePermission()
  const {
    selectedId: selectedUsername,
    setSelectedId: setSelectedUsername,
    toggleSelect: toggleSelectUsername,
  } = useRowSelection<string>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const { getWidth, startResize } = useColumnWidths(WIDTHS_TABLE_KEY)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof SettingUser> | null>(null)

  const [userDialogOpen, setUserDialogOpen] = useState(false)
  const [userDialogMode, setUserDialogMode] = useState<'new' | 'edit'>('new')
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [validDialogOpen, setValidDialogOpen] = useState(false)

  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof SettingUser
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const { data: usersData, isLoading, isFetching, error } = useGetSettingUsersQuery()
  const { data: rolesData } = useGetSettingRolesQuery()

  const [createUser, { isLoading: isCreating }] = useCreateSettingUserMutation()
  const [updateUser, { isLoading: isUpdating }] = useUpdateSettingUserMutation()
  const [changePassword, { isLoading: isChangingPassword }] = useChangeSettingUserPasswordMutation()

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'settings',
    prefKey: 'columns',
    subKey: 'accounts',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: WIDTHS_TABLE_KEY,
  })
  const { visibleColumns, openMenu } = columnsCtrl

  const baseRows = useMemo(() => usersData?.items ?? [], [usersData])

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const selectedRow = useMemo(
    () => baseRows.find((row) => row.username === selectedUsername) ?? null,
    [baseRows, selectedUsername],
  )

  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () =>
      popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const roleOptions = useMemo(() => rolesData?.items ?? [], [rolesData])
  const hasSelection = Boolean(selectedRow)

  const toggleSort = (key: keyof SettingUser) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  useEffect(() => {
    if (selectedUsername && !baseRows.some((row) => row.username === selectedUsername)) {
      setSelectedUsername(null)
    }
  }, [baseRows, selectedUsername])

  const handleOpenUserDialog = (mode: 'new' | 'edit') => {
    if (mode === 'edit' && !selectedRow) return
    setUserDialogMode(mode)
    setUserDialogOpen(true)
  }

  const handleOpenPasswordDialog = () => {
    if (!selectedRow) return
    setPasswordDialogOpen(true)
  }

  const handleOpenDeleteDialog = () => {
    if (!selectedRow) return
    setDeleteDialogOpen(true)
  }

  const handleOpenValidDialog = () => {
    if (!selectedRow) return
    setValidDialogOpen(true)
  }

  const handleSaveUser = async (form: SettingsUserDialogForm) => {
    const isNew = userDialogMode === 'new'
    if (isNew) {
      const payload: SettingUserCreatePayload = {
        email: form.email.trim(),
        password: form.password,
        confirmPassword: form.confirmPassword,
        fullName: form.fullName.trim(),
        phone: form.phone.trim() ? form.phone.trim() : null,
        roleCode: form.roleCode.trim(),
        description: form.description.trim() ? form.description.trim() : null,
      }
      const saved = await createUser(payload).unwrap()
      setSelectedUsername(saved.username)
      return
    }

    if (!selectedRow) {
      throw new Error('Please choose a record!')
    }

    const payload: SettingUserUpdatePayload = {
      fullName: form.fullName.trim(),
      phone: form.phone.trim() ? form.phone.trim() : null,
      roleCode: form.roleCode.trim(),
      description: form.description.trim() ? form.description.trim() : null,
    }
    const saved = await updateUser({ email: selectedRow.email, body: payload }).unwrap()
    setSelectedUsername(saved.username)
  }

  const handleSavePassword = async (payload: { password: string; confirmPassword: string }) => {
    if (!selectedRow) {
      throw new Error('Please choose a record!')
    }
    await changePassword({
      email: selectedRow.email,
      body: payload,
    }).unwrap()
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={() => handleOpenUserDialog('new')}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              title="New"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!hasSelection || !canEdit}
              onClick={() => handleOpenUserDialog('edit')}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              sx={actionToolbarBtnSx}
              disabled={!hasSelection || !canEdit}
              onClick={handleOpenDeleteDialog}
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              title="Delete"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!hasSelection || !canEdit}
              onClick={handleOpenPasswordDialog}
              startIcon={<LockResetIcon sx={{ fontSize: 16 }} />}
              title="Change Password"
            >
              {/* sm: short text "Password" — md+: full "Change Password" */}
              <Box component="span" sx={SHOW_ON_TABLET_INLINE}>Password</Box>
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Change Password</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!hasSelection || !canEdit}
              onClick={handleOpenValidDialog}
              startIcon={<VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Valid/Invalid Account"
            >
              {/* sm: short "Valid/Invalid" — md+: full "Valid/Invalid Account" */}
              <Box component="span" sx={SHOW_ON_TABLET_INLINE}>Valid/Invalid</Box>
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Valid/Invalid Account</Box>
            </Button>
          </Stack>
        }
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e: MouseEvent<HTMLElement>) => filterPopover.open('search', e)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      <FilterPopover
        open={filterPopover.state !== null}
        anchorEl={filterPopover.state?.anchor ?? null}
        onClose={() => filterPopover.close()}
        maxWidth={320}
      >
        {filterPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => {
              setSearchTerm(v)
              setPage(0)
            }}
            placeholder="Username, email, role, name..."
            label="Search users"
            onClear={() => filterPopover.close()}
          />
        )}
      </FilterPopover>

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={closeColPopover}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(String(colPopover.key), v)
          setPage(0)
        }}
        options={popoverOptions}
      />

      <FuturesColumnsMenu controller={columnsCtrl} />

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            flexWrap: 'wrap',
            minHeight: 30,
            py: 0.125,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: { xs: 280, sm: 320 }, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Users"
              description={extractApiErrorMessage(error)}
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState title="No Users found" description="Try adjusting the search above." />
          ) : (
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {visibleColumns.map((col) => {
                  const w = getWidth(col.key as string)
                  return (
                    <TableCell
                      key={col.key as string}
                      align={col.align}
                      className="app-table-header-cell"
                      onClick={() => toggleSort(col.key)}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                    >
                      {col.label}
                      {getTableSortSuffix(sortState, col.key)}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(String(col.key))}
                        onClick={(position) => setColPopover({ key: col.key, position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                      <ResizeHandle onMouseDown={(e) => startResize(col.key as string, e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: SettingUser) =>
              visibleColumns.map((col) => {
                const val = cellValue(row, col)
                const w = getWidth(col.key as string)
                return (
                  <TableCell
                    key={col.key as string}
                    align={col.align}
                    style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {val || '-'}
                    </Box>
                  </TableCell>
                )
              })
            }
            components={{
              /* eslint-disable @typescript-eslint/no-explicit-any */
              Table: (props: any) => (
                <Table {...props} size="small" stickyHeader sx={{ minWidth: Math.max(960, visibleColumns.length * 120), width: '100%' }} />
              ),
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const rowUsername = Number.isFinite(idx) ? rows[idx]?.username : null
                return (
                  <TableRow
                    {...props}
                    hover
                    selected={rowUsername != null && rowUsername === selectedUsername}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (rowUsername != null) toggleSelectUsername(rowUsername)
                    }}
                  />
                )
              },
              /* eslint-enable @typescript-eslint/no-explicit-any */
            }}
          />
          )}
        </Box>
      </Box>

      <SettingsUserDialog
        open={userDialogOpen}
        mode={userDialogMode}
        initialUser={selectedRow}
        roles={roleOptions}
        onClose={() => setUserDialogOpen(false)}
        onSubmit={handleSaveUser}
        isSubmitting={isCreating || isUpdating}
      />
      <SettingsChangePasswordDialog
        open={passwordDialogOpen}
        email={selectedRow?.email ?? ''}
        onClose={() => setPasswordDialogOpen(false)}
        onSubmit={handleSavePassword}
        isSubmitting={isChangingPassword}
      />
      <SettingsUserDeleteDialog
        open={deleteDialogOpen}
        email={selectedRow?.email ?? ''}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          setSelectedUsername(null)
          setDeleteDialogOpen(false)
        }}
        canEdit={canEdit}
      />
      <SettingsUserValidDialog
        open={validDialogOpen}
        email={selectedRow?.email ?? ''}
        nextValidText={selectedRow?.validText === 'Valid' ? 'invalid' : 'valid'}
        onClose={() => setValidDialogOpen(false)}
        onConfirmed={() => {
          setValidDialogOpen(false)
        }}
        canEdit={canEdit}
      />
    </Box>
  )
}
